import { getIn } from "formik";
import { PatientConnectionTypeCodes } from "csd.phoenix.models/PatientConnectionTypeCodes";
import { RelationshipToPolicyHolderTypes } from "csd.phoenix.models/RelationshipToPolicyHolderTypes";
import { IPrefillData } from '../../../../stores/formsStore';
import { IFormContext } from "../FormContext";

enum Section { None = 0, Person = 1, Rp = 2, Ec = 4, Pi = 8, Si = 16 };

const patientSectionFields = ["Patient.Person.FirstName", "Patient.Person.LastName", "Patient.Person.MiddleName",
  "Patient.Person.Title", "Patient.Person.Nickname", "Patient.Person.SSN", "Patient.Person.Birthdate",
  "Patient.Person.GenderCode", "Patient.Person.EmailAddress", "Patient.Person.HomeAddress.AddressLine1",
  "Patient.Person.HomeAddress.AddressLine2", "Patient.Person.HomeAddress.AddressLine3", "Patient.Person.HomeAddress.City",
  "Patient.Person.HomeAddress.StateProvinceCode", "Patient.Person.HomeAddress.PostalZipCode", "Patient.Person.MobilePhone.Number",
  "Patient.Person.HomePhone.Number", "Patient.Person.WorkPhone.Number", "Patient.Person.WorkPhone.Ext"]

const emergPatientRelatedFields = ["Patient.EmergencyContact.FirstName", "Patient.EmergencyContact.LastName",
  "Patient.EmergencyContact.MobilePhone.Number", "Patient.EmergencyContact.HomePhone.Number",
  "Patient.EmergencyContact.WorkPhone.Number", "Patient.EmergencyContact.WorkPhone.Ext"];

const insPatientRelatedFields = ["Patient.*.Insured.FirstName", "Patient.*.Insured.LastName",
  "Patient.*.Insured.MiddleName", "Patient.*.Insured.Birthdate"];

const insNonPatientRelatedFields = ["Patient.*.InsuredId", "Patient.*.PlanName",
  "Patient.*.GroupNum", "Patient.*.PayerName", "Patient.*.Address.AddressLine1",
  "Patient.*.Address.AddressLine2", "Patient.*.AddressLine3", "Patient.*.Address.City",
  "Patient.*.Address.StateProvinceCode", "Patient.*.Address.PostalZipCode"];

const respPartyPatientRelatedFields = ["Patient.ResponsibleParty.FirstName", "Patient.ResponsibleParty.LastName",
  "Patient.ResponsibleParty.MiddleName", "Patient.ResponsibleParty.Title", "Patient.ResponsibleParty.Nickname",
  "Patient.ResponsibleParty.SSN", "Patient.ResponsibleParty.Birthdate", "Patient.ResponsibleParty.GenderCode",
  "Patient.ResponsibleParty.HomeAddress.AddressLine1", "Patient.ResponsibleParty.HomeAddress.AddressLine2",
  "Patient.ResponsibleParty.HomeAddress.AddressLine3", "Patient.ResponsibleParty.HomeAddress.City",
  "Patient.ResponsibleParty.HomeAddress.StateProvinceCode", "Patient.ResponsibleParty.HomeAddress.PostalZipCode",
  "Patient.ResponsibleParty.MobilePhone.Number", "Patient.ResponsibleParty.HomePhone.Number", "Patient.ResponsibleParty.WorkPhone.Number",
  "Patient.ResponsibleParty.WorkPhone.Ext"];

export function handleFieldChanged(
  fieldName: string, value: unknown, oldValue: unknown,
  values: IPrefillData,
  setFieldValue: (field: string, value: any) => void,
  context: IFormContext) {

  //console.log(`Field Changed [${fieldName}]: ${oldValue} --> ${value}`);

  const copyFieldValue = (srcFieldName: string, destFieldName: string) => {
    (srcFieldName === fieldName) ? setFieldValue(destFieldName, value) : setFieldValue(destFieldName, getIn(values, srcFieldName));
  }

  const copyPersonFieldsToSection = (destSections: Section, sourcePath?: string) => {
    const isSelfEc = Boolean(destSections & Section.Ec);
    const isSelfRp = Boolean(destSections & Section.Rp);
    const isSelfPi = Boolean(destSections & Section.Pi);
    const isSelfSi = Boolean(destSections & Section.Si);
    const copyFromFieldNames: string[] = sourcePath ? [sourcePath]
      : patientSectionFields;

    copyFromFieldNames.forEach(fieldPath => {

       let fieldName = (fieldPath.includes("Number") || fieldPath.includes("Address"))
        ? determineFieldName(fieldPath, false) : determineFieldName(fieldPath, true);

      let filteredItem = respPartyPatientRelatedFields.filter(str => str.includes(fieldName))[0];
      filteredItem && isSelfRp && copyFieldValue(fieldPath, filteredItem);

      filteredItem = emergPatientRelatedFields.filter(str => str.includes(fieldName))[0];
      filteredItem && isSelfEc && copyFieldValue(fieldPath, filteredItem);

      filteredItem = insPatientRelatedFields.filter(str => str.includes(fieldName))[0];
      filteredItem && isSelfPi && copyFieldValue(fieldPath, filteredItem.replace("*", "PrimaryInsurance"));
      filteredItem && isSelfSi && copyFieldValue(fieldPath, filteredItem.replace("*", "SecondaryInsurance"));
    });

  }

  const clearInsSection = (IsPrimary: boolean) => {
    // const sectionStr = `Patient.${IsPrimary ? "Primary" : "Secondary"}Insurance.`;
    const sectionStr = `${IsPrimary ? "Primary" : "Secondary"}Insurance`;
    const sectionFields: string[] = [];
    let fieldPaths = insPatientRelatedFields.concat(insNonPatientRelatedFields);

    fieldPaths.forEach(fieldName => {
      sectionFields.push(fieldName.replace("*", sectionStr));
    })

    clearFields(sectionFields);

  }

  const clearFields = (fieldNames: string[]) => {
    // TODO - Prevent this from triggering validation.
    // May need to revisit this due to setting everything to null.
    fieldNames.forEach(fieldName => {
      // Phone field types should be set to empty strings.
      setFieldValue(fieldName, fieldName.includes("Phone") ? "" : null);

    });

  }

  switch (fieldName) {
    case "Patient.Person.FirstName":
    case "Patient.Person.LastName":
    case "Patient.Person.MiddleName":
    case "Patient.Person.Birthdate":
    case "Patient.Person.Title":
    case "Patient.Person.Nickname":
    case "Patient.Person.SSN":
    case "Patient.Person.GenderCode":
    case "Patient.Person.HomeAddress.AddressLine1":
    case "Patient.Person.HomeAddress.AddressLine2":
    case "Patient.Person.HomeAddress.AddressLine3":
    case "Patient.Person.HomeAddress.City":
    case "Patient.Person.HomeAddress.PostalZipCode":
    case "Patient.Person.HomeAddress.StateProvinceCode":
    case "Patient.Person.MobilePhone.Number":
    case "Patient.Person.HomePhone.Number":
    case "Patient.Person.WorkPhone.Number":
    case "Patient.Person.WorkPhone.Ext":
      copyPersonFieldsToSection(determineSectionOptions(values), fieldName);
      break;

    case "Patient.EmergencyContact.RelationshipCode":

      if (value === PatientConnectionTypeCodes.Self) {
        clearFields(emergPatientRelatedFields);
        copyPersonFieldsToSection(Section.Ec);
        disableSection(context, emergPatientRelatedFields, true);
      }
      else {
        disableSection(context, emergPatientRelatedFields, false);
        (oldValue === PatientConnectionTypeCodes.Self) && clearFields(emergPatientRelatedFields);;
      }
      break;

    case "Patient.ResponsibleParty.RelationshipCode":

      if (value === PatientConnectionTypeCodes.Self) {
        clearFields(respPartyPatientRelatedFields);
        copyPersonFieldsToSection(Section.Rp);
        disableSection(context, respPartyPatientRelatedFields, true);
      }
      else {
        disableSection(context, respPartyPatientRelatedFields, false);
        (oldValue === PatientConnectionTypeCodes.Self) && clearFields(respPartyPatientRelatedFields);
      }
      break;

    case "Patient.PrimaryInsurance.Insured.RelationshipCode":
    case "Patient.SecondaryInsurance.Insured.RelationshipCode": {
      const isPrimary = fieldName.includes("Primary") ? true : false;

      if (value === RelationshipToPolicyHolderTypes.Self) {
        const primaryOrSecondary = (isPrimary ? Section.Pi : Section.Si);

        clearInsSection(isPrimary);
        copyPersonFieldsToSection(primaryOrSecondary)
        // Need to enable the fields first to handle the case where someone reselected
        // the undefined value and then selected self again. If we don't all fields will
        // remain disabled.
        disableInsSection(context, isPrimary, false, true);
        // Now only enable the fields that where not copied from the patient section.
        disableInsSection(context, isPrimary, true, false);
      }
      else {
        ((oldValue === PatientConnectionTypeCodes.Self) || (value === undefined)) && clearInsSection(isPrimary);
        (value === undefined ? disableInsSection(context, isPrimary, true, true)
          : disableInsSection(context, isPrimary, false, true));
      }
    }
      break;

  }

}

const disableSection = (context: IFormContext, sectionFields: string[], disable: boolean = false) => {
  sectionFields.forEach(fieldName => {
    context.setFieldPropOverrides(fieldName, { disabled: disable });
  })
}

const disableInsSection = (context: IFormContext, isPrimary: boolean, disable: boolean = false,
  fullSectionApply = true) => {
  //const rootBinding = `Patient.${(isPrimary ? "Primary" : "Secondary")}Insurance`;
  const rootBinding = `${(isPrimary ? "Primary" : "Secondary")}Insurance`;

  let insFields = insPatientRelatedFields;

  if (fullSectionApply) {
    insFields = insFields.concat(insNonPatientRelatedFields);
  }

  insFields.forEach(fieldName => {
    fieldName = fieldName.replace("*", rootBinding);
    context.setFieldPropOverrides(fieldName, { disabled: disable });
  })

}

function determineSectionOptions(values: IPrefillData) {
  let selSections: Section = Section.None;

  if (getIn(values, "Patient.EmergencyContact.RelationshipCode") === PatientConnectionTypeCodes.Self) {
    selSections = selSections | Section.Ec;
  }

  if (getIn(values, "Patient.ResponsibleParty.RelationshipCode") === PatientConnectionTypeCodes.Self) {
    selSections = selSections | Section.Rp;
  }
  if (getIn(values, "Patient.PrimaryInsurance.Insured.RelationshipCode") === RelationshipToPolicyHolderTypes.Self) {
    selSections = selSections | Section.Pi;
  }
  if (getIn(values, "Patient.SecondaryInsurance.Insured.RelationshipCode") === RelationshipToPolicyHolderTypes.Self) {
    selSections = selSections | Section.Si;
  }
  return selSections;

}

function determineFieldName(fieldPath: string, lastIndexOfPeriod: boolean = true) {
  // if it is phone or address type fields, we want the second to the last position.
  // For all others, we want the last index of.
  const indexOfFieldName = lastIndexOfPeriod ? fieldPath.lastIndexOf(".") + 1
    : fieldPath.lastIndexOf(".", fieldPath.lastIndexOf(".") - 1) + 1;

  return fieldPath.substr(indexOfFieldName, fieldPath.length - indexOfFieldName);
}

export function handleFormLoaded(values: IPrefillData, context: IFormContext) {
  const ecRelCode = getIn(values, "Patient.EmergencyContact.RelationshipCode");
  if (typeof ecRelCode === 'undefined' || ecRelCode === PatientConnectionTypeCodes.Self) {
    disableSection(context, emergPatientRelatedFields, true);
  }

  const rpRelCode = getIn(values, "Patient.ResponsibleParty.RelationshipCode");
  if (typeof rpRelCode === 'undefined' || rpRelCode === PatientConnectionTypeCodes.Self) {
    disableSection(context, respPartyPatientRelatedFields, true);
  }

  const piRelCode = getIn(values, "Patient.PrimaryInsurance.Insured.RelationshipCode");
  if (typeof piRelCode === 'undefined') {
    disableInsSection(context, true, true);
  }
  else if (piRelCode === RelationshipToPolicyHolderTypes.Self) {
    disableInsSection(context, true, true, false);
  }

  const siRelCode = getIn(values, "Patient.SecondaryInsurance.Insured.RelationshipCode");
  if (typeof siRelCode === 'undefined') {
    disableInsSection(context, false, true);
  }
  else if (siRelCode === RelationshipToPolicyHolderTypes.Self) {
    disableInsSection(context, false, true, false);
  }
}
